import { AppIcon, type IconDefinition } from '@/components/ui/app-icon';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DynamicIcon } from '@/components/ui/icons/dynamic-icon';
import {
  iconArrowDown,
  iconArrowRotateLeft,
  iconArrowUp,
  iconArrowUpRight,
  iconEllipsis,
  iconGrid2,
  iconList,
  iconPencil,
  iconPlus,
  iconShareNodes,
  iconThumbtack,
  iconThumbtackSlash,
  iconXmark,
} from '@/lib/icons';
import { cn } from '@/lib/utils';
import { useNavigate } from '@tanstack/react-router';
import { ShareChatDialog } from '@/containers/copilot/share-chat-dialog.component';
import { useCluChatSharingEnabled } from '@/store/features/clu-sharing.feature';
import {
  forwardRef,
  useRef,
  useState,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useSidebarDrag } from './sidebar-dnd.util';
import {
  isMenuKey,
  MENU_LABELS,
  menuActions,
  openItemMenu,
  useSidebarPins,
  type SidebarMenuActionId,
  type SidebarMenuEndId,
  type SidebarMenuPlace,
  type SidebarPins,
} from './sidebar-menu.util';
import {
  pagesSwitch,
  pagesToRows,
  pagesToTiles,
  pinnedChatIds,
  refKey,
  type SidebarRef,
} from './sidebar-layout.util';
import { SidebarTooltip } from './sidebar-tooltip.component';
import { SIDEBAR_HOVER, SIDEBAR_ICON } from './tabs-sidebar.styles';

const ICONS: Record<SidebarMenuActionId | SidebarMenuEndId, IconDefinition> = {
  rename: iconPencil,
  share: iconShareNodes,
  'open-agent': iconArrowUpRight,
  'move-to-favorites': iconArrowUp,
  'move-to-pinned': iconArrowDown,
  pin: iconThumbtack,
  close: iconXmark,
  unpin: iconThumbtackSlash,
  add: iconPlus,
  'pages-as-rows': iconList,
  'pages-as-tiles': iconGrid2,
  reset: iconArrowRotateLeft,
};

interface MenuEntry {
  id: SidebarMenuActionId | Exclude<SidebarMenuEndId, 'add'>;
  onSelect: () => void;
  disabled?: boolean;
}

/** The parts of a menu: the right-click menu's, or a button's dropdown's. */
interface MenuKit {
  Item: typeof ContextMenuItem | typeof DropdownMenuItem;
  Separator: typeof ContextMenuSeparator | typeof DropdownMenuSeparator;
  Sub: typeof ContextMenuSub | typeof DropdownMenuSub;
  SubTrigger: typeof ContextMenuSubTrigger | typeof DropdownMenuSubTrigger;
  SubContent: typeof ContextMenuSubContent | typeof DropdownMenuSubContent;
}

const CONTEXT_MENU: MenuKit = {
  Item: ContextMenuItem,
  Separator: ContextMenuSeparator,
  Sub: ContextMenuSub,
  SubTrigger: ContextMenuSubTrigger,
  SubContent: ContextMenuSubContent,
};

const DROPDOWN_MENU: MenuKit = {
  Item: DropdownMenuItem,
  Separator: DropdownMenuSeparator,
  Sub: DropdownMenuSub,
  SubTrigger: DropdownMenuSubTrigger,
  SubContent: DropdownMenuSubContent,
};

const menuIcon = (icon: IconDefinition) => (
  <AppIcon
    icon={icon}
    className='text-muted-foreground'
    style={{ fontSize: 13 }}
  />
);

/**
 * An entry for each page missing from the sidebar, with its icon; one pins
 * the page at the end of the rows, and focus goes to its row once the menu
 * closes. Nothing changes until the saved layout is back.
 */
function AddPageItems({
  pins,
  Item,
}: {
  pins: SidebarPins;
  Item: MenuKit['Item'];
}) {
  const { layout, missingPages, holdFocus } = pins;
  return (
    <>
      {missingPages.map((destination) => {
        const ref: SidebarRef = { kind: 'page', id: destination.id };
        return (
          <Item
            key={destination.id}
            disabled={!layout.ready}
            onSelect={() => {
              holdFocus(refKey(ref));
              layout.pin(ref);
            }}
          >
            <DynamicIcon
              name={destination.iconName}
              size={13}
              className='text-muted-foreground'
            />
            {destination.label}
          </Item>
        );
      })}
    </>
  );
}

/**
 * The item's own entries, then, after a separator, Add to sidebar while a
 * page is missing, the pages switch while a page is pinned, and Reset sidebar
 * last.
 */
function MenuEntries({
  entries,
  pagesSwitch,
  reset,
  pins,
  kit,
}: {
  entries: MenuEntry[];
  pagesSwitch: MenuEntry | null;
  reset: MenuEntry;
  pins: SidebarPins;
  kit: MenuKit;
}) {
  const { Item, Separator, Sub, SubTrigger, SubContent } = kit;
  const row = (entry: MenuEntry) => (
    <Item key={entry.id} disabled={entry.disabled} onSelect={entry.onSelect}>
      {menuIcon(ICONS[entry.id])}
      {MENU_LABELS[entry.id]}
    </Item>
  );
  return (
    <>
      {entries.map(row)}
      <Separator />
      {pins.missingPages.length > 0 && (
        <Sub>
          <SubTrigger
            disabled={!pins.layout.ready}
            className='data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
          >
            {menuIcon(ICONS.add)}
            {MENU_LABELS.add}
          </SubTrigger>
          <SubContent className='max-h-80 w-48 overflow-y-auto'>
            <AddPageItems pins={pins} Item={Item} />
          </SubContent>
        </Sub>
      )}
      {pagesSwitch && row(pagesSwitch)}
      {row(reset)}
    </>
  );
}

/**
 * A sidebar item's menu, on right click, on the menu key or Shift+F10 while
 * the item has focus, and on a finger held on it and let go (see
 * SidebarDnd). A row's menu is on a "..." button too, which shows on hover
 * or focus, and always without hover; a tile has none, nor anything folded.
 * A chat whose title shows in full has Rename, as F2 does too: the title
 * turns into a field once the menu has closed. A chat has Share where
 * clu_chat_sharing_v1 is on. Outside the tabbed sidebar's pins context it
 * renders the item alone.
 */
export const SidebarItemMenu = forwardRef<
  HTMLDivElement,
  {
    sidebarRef: SidebarRef;
    label: string;
    place: SidebarMenuPlace;
    /** A "..." button at the right end, for an open row. */
    button?: boolean;
    /** Turns a chat's title into a field, for Rename and F2. */
    onRename?: () => void;
    /** The title is a field: no menu until it is done. */
    editing?: boolean;
    children: ReactNode;
  } & HTMLAttributes<HTMLDivElement>
>(function SidebarItemMenu(
  {
    sidebarRef,
    label,
    place,
    button = false,
    onRename,
    editing = false,
    className,
    children,
    onKeyDown,
    ...rest
  },
  ref
) {
  const pins = useSidebarPins();
  const navigate = useNavigate();
  const { activeKey } = useSidebarDrag();
  // Rename waits for the menu to close, so the field keeps the focus the
  // closing menu would give back.
  const renameOnClose = useRef(false);
  const sharingOn = useCluChatSharingEnabled();
  const [sharing, setSharing] = useState(false);
  if (!pins)
    return (
      <div ref={ref} className={className} onKeyDown={onKeyDown} {...rest}>
        {children}
      </div>
    );
  const { layout, closeChat, reopenChats, holdFocus, restoreFocus } = pins;
  // Moving or removing the item unmounts it, menu and all: focus follows it
  // to where it lands, or to its neighbour, once the menu closes.
  const key = refKey(sidebarRef);
  const moving = (action: () => void) => (): void => {
    holdFocus(key);
    action();
  };
  const onCloseAutoFocus = (event: Event) => {
    if (renameOnClose.current && onRename) {
      renameOnClose.current = false;
      event.preventDefault();
      onRename();
      return;
    }
    if (restoreFocus()) event.preventDefault();
  };
  // From the keyboard, the menu opens at the item's bottom left corner.
  const openFromKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    // The "..." menu's keys reach here too, through React from its portal:
    // only a key from within the item acts, and none while an item is being
    // moved or the title is a field.
    if (!event.currentTarget.contains(event.target as Node)) return;
    if (activeKey !== null || editing) return;
    if (event.key === 'F2' && onRename) {
      event.preventDefault();
      onRename();
      return;
    }
    if (!isMenuKey(event)) return;
    event.preventDefault();
    const { left, bottom } = event.currentTarget.getBoundingClientRect();
    openItemMenu(event.currentTarget, { x: left, y: bottom });
  };

  const run: Record<SidebarMenuActionId, () => void> = {
    rename: () => {
      renameOnClose.current = true;
    },
    share: () => setSharing(true),
    'open-agent': () =>
      void navigate({
        to: '/agents/$agentId',
        params: { agentId: sidebarRef.id },
      }),
    'move-to-favorites': moving(() => layout.place(sidebarRef, 'favorites')),
    'move-to-pinned': moving(() => layout.place(sidebarRef, 'pinned')),
    pin: moving(() => layout.pin(sidebarRef)),
    close: moving(() => closeChat(sidebarRef.id)),
    // An unpinned chat goes back to the tabs, as Arc puts it on top.
    unpin: moving(() => {
      if (sidebarRef.kind === 'chat') reopenChats([sidebarRef.id]);
      layout.unpin(sidebarRef);
    }),
  };
  const reset = moving(() => {
    reopenChats(pinnedChatIds(layout.layout));
    layout.reset();
  });
  // Until the saved layout is back, nothing that changes it is offered.
  const changesLayout = (id: SidebarMenuActionId) =>
    id !== 'open-agent' && id !== 'close' && id !== 'rename' && id !== 'share';
  const entries: MenuEntry[] = menuActions(sidebarRef, place, {
    rename: onRename !== undefined,
    share: sharingOn,
  }).map((id) => ({
    id,
    onSelect: run[id],
    disabled: changesLayout(id) && !layout.ready,
  }));
  // Every page between the tile grid and the rows at once.
  const switchTo = pagesSwitch(layout.layout);
  const pagesSwitchEntry: MenuEntry | null = switchTo && {
    id: switchTo === 'rows' ? 'pages-as-rows' : 'pages-as-tiles',
    onSelect: moving(() =>
      layout.arrange(switchTo === 'rows' ? pagesToRows : pagesToTiles)
    ),
    disabled: !layout.ready,
  };
  const resetEntry: MenuEntry = {
    id: 'reset',
    onSelect: reset,
    disabled: layout.isDefault || !layout.ready,
  };

  return (
    <>
      <ContextMenu modal={false}>
        <ContextMenuTrigger asChild disabled={editing}>
          <div
            ref={ref}
            // A grid caller's one column shrinks with the sidebar, so a long
            // title truncates instead of widening the row past the edge.
            className={cn(
              'group/pin relative min-w-0 [grid-template-columns:minmax(0,1fr)]',
              className
            )}
            {...rest}
            onKeyDown={openFromKeyboard}
          >
            {children}
            {button && !editing && (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <button
                    type='button'
                    aria-label={`Actions for ${label}`}
                    className={cn(
                      'absolute grid place-items-center rounded-md opacity-0 transition-opacity',
                      'hover:bg-black/[0.07] focus:outline-hidden focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-primary/50 dark:hover:bg-white/[0.1]',
                      'group-focus-within/pin:opacity-100 group-hover/pin:opacity-100 data-[state=open]:opacity-100',
                      // Without hover, as on a touch screen, it always shows.
                      '[@media(hover:none)]:opacity-100',
                      'top-1/2 right-1 h-6 w-6 -translate-y-1/2',
                      SIDEBAR_ICON
                    )}
                  >
                    <AppIcon icon={iconEllipsis} style={{ fontSize: 13 }} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align='start'
                  className='w-48'
                  onCloseAutoFocus={onCloseAutoFocus}
                >
                  <MenuEntries
                    entries={entries}
                    pagesSwitch={pagesSwitchEntry}
                    reset={resetEntry}
                    pins={pins}
                    kit={DROPDOWN_MENU}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent
          className='w-48'
          onCloseAutoFocus={onCloseAutoFocus}
          // On Windows the menu key sends its contextmenu as the key comes up,
          // once the key has opened this menu and moved focus into it: the
          // browser's own menu stays shut over it.
          onContextMenu={(event) => event.preventDefault()}
        >
          <MenuEntries
            entries={entries}
            pagesSwitch={pagesSwitchEntry}
            reset={resetEntry}
            pins={pins}
            kit={CONTEXT_MENU}
          />
        </ContextMenuContent>
      </ContextMenu>
      {sharing && (
        <ShareChatDialog
          chat={{ id: sidebarRef.id, title: label }}
          onClose={() => setSharing(false)}
        />
      )}
    </>
  );
});

/**
 * A "+" after the pinned rows, with the pages missing from the sidebar: the
 * item menus' Add to sidebar, one step closer. It shows on hover or focus of
 * the rows, and always without hover; none when nothing is missing.
 */
export function AddPageButton() {
  const pins = useSidebarPins();
  if (!pins || pins.missingPages.length === 0) return null;
  const onCloseAutoFocus = (event: Event) => {
    if (pins.restoreFocus()) event.preventDefault();
  };
  return (
    <DropdownMenu modal={false}>
      <SidebarTooltip label={MENU_LABELS.add}>
        <DropdownMenuTrigger asChild>
          <button
            type='button'
            aria-label={MENU_LABELS.add}
            className={cn(
              'mt-0.5 ml-1 grid h-6 w-6 shrink-0 place-items-center self-start rounded-md opacity-0 transition-opacity',
              'focus:outline-hidden focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-primary/50',
              // Open, it keys off aria-expanded: the tooltip's trigger sets
              // its own data-state over the menu's.
              'group-focus-within/pinned:opacity-100 group-hover/pinned:opacity-100 aria-expanded:opacity-100',
              // Without hover, as on a touch screen, it always shows.
              '[@media(hover:none)]:opacity-100',
              SIDEBAR_ICON,
              SIDEBAR_HOVER
            )}
          >
            <AppIcon icon={iconPlus} style={{ fontSize: 13 }} />
          </button>
        </DropdownMenuTrigger>
      </SidebarTooltip>
      <DropdownMenuContent
        align='start'
        className='w-48'
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <AddPageItems pins={pins} Item={DropdownMenuItem} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
